import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Switch, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import moment from "moment";
import { getCheckInSettingsApi, updateCheckInSettingsApi } from "src/DAL/coachWorkspace/coachWorkspace";
import { CW, ErrorBox, InfoHint, Loading, PageTitle, Panel } from "./cwUi";

/**
 * Check-in settings for ALL journeys (Nov 2026 — were per journey).
 * NEW admin path /check-ins/settings (nav: Trackers → Check-in settings).
 *  - Members can submit a missed check-in late   (default On, as before)
 *  - Coaches can change a client's check-in day   (default Off)
 * The check-in time stays per journey (journey → Check-in rules).
 */

function SettingRow({ title, hint, checked, onChange, label }) {
  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        alignItems: { xs: "flex-start", sm: "center" },
        justifyContent: "space-between",
        flexDirection: { xs: "column", sm: "row" },
        py: 2,
        borderTop: `1px solid ${CW.line}`,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: CW.ink }}>{title}</Typography>
        <Typography sx={{ fontSize: 13, color: CW.muted, mt: 0.25 }}>{hint}</Typography>
      </Box>
      <Switch checked={checked} onChange={(e) => onChange(e.target.checked)} inputProps={{ "aria-label": label }} />
    </Box>
  );
}

export default function CheckInSettingsPage() {
  const { enqueueSnackbar } = useSnackbar();
  const [saved, setSaved] = useState(null);
  const [form, setForm] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError("");
    const result = await getCheckInSettingsApi();
    if (result?.code === 200) {
      setSaved(result.settings);
      setForm({
        allowMissedCheckIns: Boolean(result.settings.allowMissedCheckIns),
        allowCheckInDayChange: Boolean(result.settings.allowCheckInDayChange),
      });
    } else setError(result?.message || "Could not load check-in settings");
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (error && !saved) return <ErrorBox message={error} onRetry={load} />;
  if (!saved || !form) return <Loading />;

  const changed =
    form.allowMissedCheckIns !== Boolean(saved.allowMissedCheckIns) ||
    form.allowCheckInDayChange !== Boolean(saved.allowCheckInDayChange);

  const save = async () => {
    const body = {};
    if (form.allowMissedCheckIns !== Boolean(saved.allowMissedCheckIns)) body.allowMissedCheckIns = form.allowMissedCheckIns;
    if (form.allowCheckInDayChange !== Boolean(saved.allowCheckInDayChange)) body.allowCheckInDayChange = form.allowCheckInDayChange;
    setBusy(true);
    const result = await updateCheckInSettingsApi(body);
    setBusy(false);
    if (result?.code === 200) {
      enqueueSnackbar(result.message || "Check-in settings saved", { variant: "success" });
      setSaved(result.settings);
    } else enqueueSnackbar(result?.message || "Could not save", { variant: "error" });
  };

  return (
    <div className="container-fluid">
      <Box sx={{ maxWidth: 900, mx: "auto", pb: 6 }}>
        <PageTitle>
          Check-in settings
          <InfoHint id="settings.checkin" />
        </PageTitle>
        <Typography sx={{ fontSize: 13.5, color: CW.muted, mb: 2 }}>
          For all journeys. The check-in opening time is set on each journey (journey → Check-in rules).
        </Typography>

        <Panel>
          <SettingRow
            title="Members can submit a missed check-in late"
            label="Allow late submit"
            checked={form.allowMissedCheckIns}
            onChange={(v) => setForm({ ...form, allowMissedCheckIns: v })}
            hint={
              form.allowMissedCheckIns
                ? "On: a missed check-in can still be filled in after its window closes (as before)."
                : "Off: when the next check-in opens, the previous one closes — no late submit, no reminders for it."
            }
          />
          <SettingRow
            title="Coaches can change a client's check-in day"
            label="Allow day change"
            checked={form.allowCheckInDayChange}
            onChange={(v) => setForm({ ...form, allowCheckInDayChange: v })}
            hint={
              form.allowCheckInDayChange
                ? "On: coaches see “Change day” for their clients on weekly journeys (preview first; past check-ins keep their dates)."
                : "Off: every client checks in on the journey's day."
            }
          />
          {changed && form.allowMissedCheckIns !== Boolean(saved.allowMissedCheckIns) ? (
            <Alert severity="info" sx={{ mt: 1 }}>
              Changing late submit updates the open and missed check-ins of every live journey (takes a minute).
            </Alert>
          ) : null}
          <Box sx={{ display: "flex", gap: 1, justifyContent: "space-between", alignItems: "center", mt: 2, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: 12, color: CW.muted }}>
              {saved.updatedAt
                ? `Last saved ${moment(saved.updatedAt).format("D MMM YYYY, HH:mm")}${saved.byName ? ` by ${saved.byName}` : ""}`
                : "Default settings (never changed)"}
              <InfoHint id="settings.save" />
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <button
                type="button"
                className="small-contained-outlined-button"
                disabled={busy || !changed}
                onClick={() =>
                  setForm({
                    allowMissedCheckIns: Boolean(saved.allowMissedCheckIns),
                    allowCheckInDayChange: Boolean(saved.allowCheckInDayChange),
                  })
                }
              >
                Reset
              </button>
              <button type="button" className="small-contained-button" disabled={busy || !changed} onClick={save}>
                Save settings
              </button>
            </Box>
          </Box>
        </Panel>
      </Box>
    </div>
  );
}
